import { useState, useMemo } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { PlaySound } from "~/SoundUtils";
import { volumeAtom, standbyModeAtom, isSettingOpenAtom } from "~/Atom";
import { talkModes } from "~/Commons";
import { useKnowledgeDocs } from "~/components/settings/HamburgerMenu/hooks/useKnowledgeDocs";

// 入力方法の種類
export type InputMethod = "url" | "file" | "text";

// ナレッジベース情報インターフェース
export interface KnowledgeBase {
  id: string;
  name: string;
}

// ナレッジドキュメント情報インターフェース
export interface KnowledgeDocument {
  id: string;
  name: string;
  url?: string;
  fileFormat?: string;
  isTextInput?: boolean;
  created_at: string | number | Date;
}

// ドキュメント詳細取得結果インターフェース
export interface DocumentDetailResult {
  content?: string;
  error?: string;
}

/**
 * ナレッジセクションのカスタムフック
 *
 * @returns
 */
export const useKnowledgeSection = () => {
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const [addingKnowledgeId, setAddingKnowledgeId] = useState("");
  const [inputMethod, setInputMethod] = useState<InputMethod>("url");
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newText, setNewText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const {
    status,
    knowledgeBases,
    allKnowledgeDocuments,
    addDocToKnowledgeBase,
    addDocByText,
    addDocByFile,
    deleteDocFromKnowledgeBase,
    getDocumentDetail,
  } = useKnowledgeDocs();

  const isLoading = status === "loading";

  /**
   * モーダルを開く
   *
   * @param setModalState
   */
  const handleModalOpen = (setModalState: React.Dispatch<React.SetStateAction<boolean>>) => {
    PlaySound("push", volume);
    setModalState(true);
  };

  /**
   * モーダルを閉じる
   *
   * @param setModalState
   */
  const handleModalClose = (setModalState: React.Dispatch<React.SetStateAction<boolean>>) => {
    PlaySound("push", volume);
    setModalState(false);
    setStandbyMode(isSettingOpen ? talkModes.stopped : talkModes.active);
  };

  /**
   * キャンセルボタンクリックハンドラ
   * フォームをリセットし、入力状態を初期化
   */
  const handleCancelClick = () => {
    setNewName("");
    setNewUrl("");
    setNewText("");
    setSelectedFile(null);
    setInputMethod("url");
    setAddingKnowledgeId("");
    setIsDragOver(false);
  };

  /**
   * ファイル検証
   *
   * @param file 検証するファイル
   * @returns
   */
  const validateFile = (file: File): boolean => {
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      alert(t("fileSizeExceeded"));
      return false;
    }
    const supportedExtensions = [
      ".txt",
      ".md",
      ".pdf",
      ".html",
      ".xlsx",
      ".xls",
      ".docx",
      ".csv",
      ".htm",
    ];
    const fileName = file.name.toLowerCase();
    const isSupported = supportedExtensions.some((ext) => fileName.endsWith(ext));
    if (!isSupported) {
      alert(t("unsupportedFileFormat"));
      return false;
    }
    return true;
  };

  /**
   * ドラッグエンター時の処理
   *
   * @param e Reactドラッグイベント
   */
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading) setIsDragOver(true);
  };

  /**
   * ドラッグリーブ時の処理
   *
   * @param e Reactドラッグイベント
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  /**
   * ドラッグオーバー時の処理
   *
   * @param e Reactドラッグイベント
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * ドラッグ&ドロップ時の処理
   *
   * @param e Reactドラッグイベント
   * @returns
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isLoading) return;
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  /**
   * 必須項目の検証
   *
   * @returns true: 必須項目がすべて入力されている, false: 入力されていない項目がある
   */
  const validateRequired = (): boolean => {
    if (inputMethod === "url") {
      return !!(newName.trim() && newUrl.trim() && addingKnowledgeId);
    }
    if (inputMethod === "text") {
      return !!(newName.trim() && newText.trim() && addingKnowledgeId);
    }
    if (inputMethod === "file") {
      return !!(newName.trim() && selectedFile && addingKnowledgeId);
    }
    return false;
  };

  /**
   * ドキュメントかどうかを判定する
   *
   * @param obj 判定対象オブジェクト
   * @returns
   */
  const isKnowledgeDocument = (obj: unknown): obj is KnowledgeDocument => {
    if (!obj || typeof obj !== "object") return false;
    const rec = obj as Record<string, unknown>;
    return (
      typeof rec.id === "string" &&
      typeof rec.name === "string" &&
      typeof rec.created_at === "number"
    ); // unixタイムスタンプ
  };

  /**
   * 型ガード: Record<string, unknown[]>かどうかを判定
   *
   * @param obj 判定対象オブジェクト
   * @returns
   */
  function isRecordOfUnknownArray(obj: unknown): obj is Record<string, unknown[]> {
    if (typeof obj !== "object" || obj === null) return false;
    return Object.values(obj).every((value) => Array.isArray(value));
  }

  /**
   * ナレッジドキュメントの正規化
   * allKnowledgeDocumentsの型がRecord<string, unknown[]>であるため、KnowledgeDocument[]にフィルタリングする
   *
   * @returns {Record<string, KnowledgeDocument[]>} ナレッジベースIDをキー、KnowledgeDocument配列を値とするオブジェクト
   */
  const normalizedDocumentsMap = useMemo(() => {
    const result: Record<string, KnowledgeDocument[]> = {};
    if (!allKnowledgeDocuments || !isRecordOfUnknownArray(allKnowledgeDocuments)) return result;
    Object.entries(allKnowledgeDocuments).forEach(([kbId, docs]) => {
      if (Array.isArray(docs)) {
        const filtered = (docs as unknown[]).filter(isKnowledgeDocument);
        result[kbId] = filtered;
      } else {
        result[kbId] = [];
      }
    });
    return result;
  }, [allKnowledgeDocuments]);

  /**
   * 名前の重複をチェックする
   *
   * @returns 重複があればtrue、なければfalse
   */
  const isDuplicateName = (): boolean => {
    if (inputMethod === "file") return false;
    const currentDocs = normalizedDocumentsMap[addingKnowledgeId] || [];
    return currentDocs.some((doc) => doc.name === newName);
  };

  /**
   * ドキュメントの追加を実行
   *
   * @returns 追加が成功した場合はtrue、失敗した場合はfalse
   */
  const performAdd = async (): Promise<boolean> => {
    switch (inputMethod) {
      case "url":
        return addDocToKnowledgeBase(newName, newUrl, addingKnowledgeId);
      case "text":
        return addDocByText(newName, newText, addingKnowledgeId);
      case "file":
        return selectedFile ? addDocByFile(selectedFile, addingKnowledgeId, newName) : false;
      default:
        return false;
    }
  };

  /**
   * 保存処理を実行
   *
   * @returns
   */
  const handleSave = async (): Promise<void> => {
    if (!validateRequired()) {
      alert(t("pleaseEnterAllFields"));
      return;
    }
    if (isDuplicateName()) {
      alert(t("nameAlreadyExists"));
      return;
    }
    try {
      const success = await performAdd();
      if (success) {
        alert(t("dataAdded"));
        handleCancelClick();
      }
    } catch (error: unknown) {
      console.error("Error adding knowledge:", error);
      const message = error instanceof Error ? error.message : String(error);
      alert(`${t("errorAddingData")}: ${message}`);
    }
  };

  /**
   * ドキュメント削除処理を実行
   *
   * @param knowledgeId ナレッジベースID
   * @param docId ドキュメントID
   * @param docName ドキュメント登録名
   */
  const handleDeleteDoc = async (knowledgeId: string, docId: string, docName: string) => {
    try {
      await deleteDocFromKnowledgeBase(knowledgeId, docId, docName);
      alert(t("documentDeletionCompleted"));
    } catch (error) {
      console.error("ドキュメント削除中にエラーが発生しました：", error);
      alert(t("errorDuringDeletion"));
    }
  };

  return {
    // data
    status,
    knowledgeBases: knowledgeBases as KnowledgeBase[],
    allKnowledgeDocuments: normalizedDocumentsMap,
    getDocumentDetail,
    // state
    addingKnowledgeId,
    setAddingKnowledgeId,
    inputMethod,
    setInputMethod,
    newName,
    setNewName,
    newUrl,
    setNewUrl,
    newText,
    setNewText,
    selectedFile,
    setSelectedFile,
    isDragOver,
    setIsDragOver,
    isLoading,
    // handlers
    handleModalOpen,
    handleModalClose,
    handleCancelClick,
    validateFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleSave,
    handleDeleteDoc,
  };
};

export type UseKnowledgeSectionReturn = ReturnType<typeof useKnowledgeSection>;
